import { createContext, type PropsWithChildren, useContext } from 'react';

import {
  type AdvancedFilterCondition,
  type AdvancedFiltersConfig,
  type AdvancedFiltersPublicState,
  type InferAdvancedFilterPublicState,
} from '../types';

interface AdvancedFiltersPublicContextProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends PropsWithChildren {
  advancedFiltersConfig: Config;
  defaultAdvancedFilters: InferAdvancedFilterPublicState<Config>;
  onAdvancedFiltersChange: (
    newAdvancedFilters: InferAdvancedFilterPublicState<Config>,
  ) => void;
}

type AdvancedFiltersPublicContextValue = {
  config: AdvancedFiltersConfig;
  defaultDraftAdvancedFilters: AdvancedFiltersPublicState;
  onCommitDraftAdvancedFilters: (state: AdvancedFiltersPublicState) => void;
};

const AdvancedFiltersPublicContext =
  createContext<AdvancedFiltersPublicContextValue | null>(null);

export const AdvancedFiltersPublicContextProvider = <
  // need to turn off contravariance check on `renderFieldOptions`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Config extends AdvancedFiltersConfig<AdvancedFilterCondition, any>,
>({
  children,
  defaultAdvancedFilters,
  advancedFiltersConfig,
  onAdvancedFiltersChange,
}: AdvancedFiltersPublicContextProps<Config>) => {
  return (
    <AdvancedFiltersPublicContext.Provider
      value={{
        config: advancedFiltersConfig,
        defaultDraftAdvancedFilters: defaultAdvancedFilters,
        onCommitDraftAdvancedFilters: onAdvancedFiltersChange as (
          state: AdvancedFiltersPublicState,
        ) => void,
      }}
    >
      {children}
    </AdvancedFiltersPublicContext.Provider>
  );
};

export const useAdvancedFiltersPublicContext = () => {
  const context = useContext(AdvancedFiltersPublicContext);
  if (!context) {
    throw new Error(
      'useAdvancedFiltersPublicContext must be used within a AdvancedFiltersPublicContextProvider',
    );
  }
  return context;
};
